import { components, paths } from "@/api-schema";
import {
  PathsV1ModelFamilySearchPostParametersQueryExtend,
  PathsV1ModelFamilySearchPostParametersQueryOrderByDirection,
  PathsV1ModelFamilySearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useModelFamilyDeleteMutation } from "@/common/api/model-family/use-model-family-delete.mutation";
import { useModelFamilySearchQuery } from "@/common/api/model-family/use-model-family-search.query";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { TableFeatures } from "@/components/data-table/types";
import { BrandFilter } from "@/components/filter/predefined/brand-filter";
import { ModelFamilyDatIdFilter } from "@/components/filter/predefined/model-family-dat-id-filter";
import { RowActionVariant } from "@/components/ui/table-row-actions";
import i18next from "i18next";
import { GitMerge } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { modelFamilyTableDefaultColumns } from "./model-family-table-columns";

type ModelFamily = components["schemas"]["ModelFamily"] & RowWithIdentifier;
type ModelFamilySorting = NonNullable<paths["/v1/model-family/search"]["post"]["parameters"]["query"]["orderBy"]>;
type ModelFamilyQueryParams = paths["/v1/model-family/search"]["post"]["parameters"]["query"];
type ModelFamilyRequestBody = paths["/v1/model-family/search"]["post"]["requestBody"]["content"]["application/json"];

const DEFAULT_COLUMNS = [
  modelFamilyTableDefaultColumns.columnName,
  modelFamilyTableDefaultColumns.columnBrand,
  modelFamilyTableDefaultColumns.columnDatId,
];

const MODEL_FAMILY_TABLE_CONFIG: PredefinedTableConfig<ModelFamilySorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1ModelFamilySearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1ModelFamilySearchPostParametersQueryOrderByField.Name,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("common:SearchModelFamilies"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: true,
    showDelete: true,
    showView: false,
  } as const,
  sortingMapping: {
    name: ["name"],
    brand: ["brand"],
    datId: ["datId"],
    createdAt: ["createdAt"],
    updatedAt: ["updatedAt"],
  } as const,
} as const;

const MODEL_FAMILY_TABLE_QUERY_HOOKS: DataFetchHooks<ModelFamily, ModelFamilyQueryParams, ModelFamilyRequestBody> = {
  useSearchQuery: useModelFamilySearchQuery,
} as const;

const DEFAULT_FILTER_VALUES: Record<string, string[]> = {
  brand: [],
  identifier: [],
};

export interface ModelFamilyTableProps extends BasePredefinedTableProps<
  ModelFamily,
  ModelFamilySorting,
  ModelFamilyQueryParams,
  ModelFamilyRequestBody
> {
  onMerge?: (modelFamily: ModelFamily) => void;
}

const BaseModelFamilyTable = createBasePredefinedTable({
  config: MODEL_FAMILY_TABLE_CONFIG,
  queryHooks: MODEL_FAMILY_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function ModelFamilyTable(props: ModelFamilyTableProps) {
  const { t } = useTranslation(["common", "masterdata", "ram"]);
  const [_, navigate] = useLocation();
  const { name = "model-family-table", showCreateButton = true, onMerge, filters, ...restProps } = props;
  const deleteMutation = useModelFamilyDeleteMutation();
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>(DEFAULT_FILTER_VALUES);
  const defaultFilters = useMemo(
    () =>
      filters ?? [
        new ModelFamilyDatIdFilter({ body: { brand: filterValues.brand ?? [] } }),
        new BrandFilter({ id: "brand" }),
      ],
    [filters, filterValues.brand],
  );

  const handleFilterChange = useCallback((filterId: string, values: string[]) => {
    setFilterValues((currentValues) => ({
      ...currentValues,
      [filterId]: values,
      ...(filterId === "brand" ? { identifier: [] } : {}),
    }));
  }, []);

  const handleEdit = useCallback(
    (modelFamily: ModelFamily) => {
      navigate(`~/masterdata/model-family/${modelFamily.uuid}/update`);
    },
    [navigate],
  );

  const handleDelete = useCallback(
    async (modelFamily: ModelFamily) => {
      await toastMutationStatus({
        mutationFn: () => deleteMutation.mutateAsync({ uuid: modelFamily.uuid }),
        successMessage: t("masterdata:ModelFamilySuccessfullyDeleted", {
          name: modelFamily.name,
        }),
        errorMessage: t("masterdata:ErrorDeletingModelFamily", {
          name: modelFamily.name,
        }),
      });
    },
    [deleteMutation, t],
  );

  const customRowActions = useCallback(
    (row: { original: ModelFamily }) => {
      if (!onMerge) return [];

      return [
        {
          label: t("masterdata:Merge"),
          icon: <GitMerge className="h-4 w-4" />,
          variant: RowActionVariant.Warning,
          onClick: () => onMerge(row.original),
        },
      ];
    },
    [onMerge, t],
  );

  return (
    <BaseModelFamilyTable
      searchPlaceholder={t("common:SearchModelFamilies")}
      extend={[
        PathsV1ModelFamilySearchPostParametersQueryExtend.Brand,
        PathsV1ModelFamilySearchPostParametersQueryExtend.ModelFamilyIdentifiers,
      ]}
      {...{
        name,
        showCreateButton,
        filters: defaultFilters,
        filterValues,
        onFilterChange: handleFilterChange,
        createButtonLink: "~/masterdata/model-family/create",
        onEdit: handleEdit,
        onDelete: handleDelete,
        customRowActions,
        confirmActions: ["delete"],
        confirmLabels: {
          delete: {
            title: t("masterdata:DeleteModelFamily"),
            text: t("masterdata:DeleteModelFamilyDescription"),
            confirmButton: t("ram:YesDelete"),
            cancelButton: t("common:Cancel"),
          },
        },
        ...restProps,
      }}
    />
  );
}
